import { useCallback, useMemo, useRef, useState } from "react"

import { useRecoilCallback, useRecoilValue, useSetRecoilState } from "recoil"

import {
  SOLAR_DESIGN_SAVE_FIELDS,
  type SolarDesignSaveBody,
  type SolarDesignSaveResultDto,
} from "~/api/dto/solar-proposal-designer.dto"
import { solarProposalService } from "~/api/services/solar-proposal.service"
import {
  SOLAR_DESIGN_SUPERSEDED_CODE,
  solarProposalErrorCode,
  solarProposalErrorMessages,
} from "~/api/solar-proposal-errors"
import type { Design } from "~/containers/Proposal/state/designs/design-model"
import { useDesignOptimisticUpdate } from "~/containers/Proposal/state/designs/design-optimistic-update"
import {
  designDiffState,
  designLocalState,
  designRemoteState,
  estimatesRemoteState,
} from "~/containers/Proposal/state/designs/design-state"
import { loadDesignIntoElements } from "~/containers/Proposal/state/designs/load-design-into-elements"
import {
  createUpdateDesignInput,
  type DesignActions,
  type DesignStatus,
} from "~/containers/Proposal/state/designs/use-design"
import { getFrom } from "~/utils/recoil"

import {
  parseDesignerDesign,
  toDesignerEstimates,
} from "./hydrate-designer-state"

/**
 * The save body: the V1 diff mapped through V1's input builder, then only the
 * fields the V2 save accepts. Equipment belongs to the Equipment step; the
 * designer here never edits it.
 */
export const toSolarDesignSaveBody = (
  delta: Partial<Design>,
  designId: string,
  expectedUpdatedAt: string,
): SolarDesignSaveBody | null => {
  const input = createUpdateDesignInput(delta, designId) as Record<
    string,
    unknown
  >
  const changes = Object.fromEntries(
    SOLAR_DESIGN_SAVE_FIELDS.filter((field) => input[field] !== undefined).map(
      (field) => [field, input[field]],
    ),
  )

  return Object.keys(changes).length > 0
    ? { expectedUpdatedAt, ...changes }
    : null
}

export type RestDesignActionsEvents = {
  /** A save or reset answered; carries the new summary and any warnings. */
  onSaved: (result: SolarDesignSaveResultDto) => void
  /** The design changed elsewhere; nothing was written. */
  onSuperseded: () => void
  /** Any other failure, with the reasons the gateway gave. */
  onFailed: (messages: string[]) => void
}

/**
 * The V2 designer's `DesignActions`: V1's local actions as they are, with save
 * and reset sent over REST under the design's version. A save keeps V1's diff
 * (`designDiffState`) and input builder and swaps only the transport; the
 * answer becomes the remote design, and V1's optimistic update brings local
 * into step, as after a V1 save. A stale version is never overwritten.
 */
export const useRestDesignActions = ({
  proposalId,
  initialVersion,
  canResetDesign,
  events,
}: {
  proposalId: string
  initialVersion: string
  canResetDesign: boolean
  events: RestDesignActionsEvents
}): {
  actions: DesignActions
  status: DesignStatus
  /** Settles when no save or reset is in flight. */
  whenIdle: () => Promise<void>
} => {
  const setLocal = useSetRecoilState(designLocalState)
  const versionRef = useRef(initialVersion)
  const eventsRef = useRef(events)
  eventsRef.current = events
  const [isSaving, setIsSaving] = useState(false)
  const [isResetting, setIsResetting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(true)
  const busyRef = useRef(false)
  const inFlightRef = useRef<Promise<void>>(Promise.resolve())

  // V1's machinery: the local design as of the last save, so the optimistic
  // update can keep what changed locally while the save was in flight.
  const local = useRecoilValue(designLocalState)
  const localRef = useRef<Design>(local)
  useDesignOptimisticUpdate(localRef)

  const applyResult = useRecoilCallback(
    ({ set }) =>
      (result: SolarDesignSaveResultDto, reloadElements: boolean) => {
        versionRef.current = result.updatedAt
        const design = parseDesignerDesign(result.design)
        set(designRemoteState, design)
        if (reloadElements) {
          loadDesignIntoElements(set, design)
        }
        set(
          estimatesRemoteState,
          toDesignerEstimates(result.installAreaProduction),
        )
      },
    [],
  )

  const reportFailure = useCallback((error: unknown) => {
    setIsSuccess(false)
    if (solarProposalErrorCode(error) === SOLAR_DESIGN_SUPERSEDED_CODE) {
      eventsRef.current.onSuperseded()
      return
    }
    const messages = solarProposalErrorMessages(error)
    eventsRef.current.onFailed(
      messages.length > 0 ? messages : [`The design couldn't be saved.`],
    )
  }, [])

  const save = useRecoilCallback(
    ({ snapshot }) =>
      async () => {
        const get = getFrom(snapshot)
        const delta = get(designDiffState)
        const snapshotLocal = get(designLocalState)
        if (!delta || busyRef.current) {
          return
        }
        const body = toSolarDesignSaveBody(
          delta,
          snapshotLocal.id,
          versionRef.current,
        )
        if (!body) {
          return
        }
        localRef.current = snapshotLocal
        busyRef.current = true
        setIsSaving(true)
        const saving = (async (): Promise<void> => {
          try {
            const result = await solarProposalService.patchDesign(
              proposalId,
              body,
            )
            applyResult(result, false)
            setIsSuccess(true)
            eventsRef.current.onSaved(result)
          } catch (error) {
            reportFailure(error)
          } finally {
            busyRef.current = false
            setIsSaving(false)
          }
        })()
        inFlightRef.current = saving
        await saving
      },
    [applyResult, proposalId, reportFailure],
  )

  const resetRemote = useCallback(() => {
    if (!canResetDesign || busyRef.current) {
      return
    }
    busyRef.current = true
    setIsResetting(true)
    inFlightRef.current = solarProposalService
      .postDesignReset(proposalId, versionRef.current)
      .then((result) => {
        applyResult(result, true)
        setIsSuccess(true)
        eventsRef.current.onSaved(result)
      })
      .catch(reportFailure)
      .finally(() => {
        busyRef.current = false
        setIsResetting(false)
      })
  }, [applyResult, canResetDesign, proposalId, reportFailure])

  const changeLocal = useCallback(
    (update: Partial<Design>) => {
      setLocal((design: Design) => ({ ...design, ...update }))
    },
    [setLocal],
  )

  const reset = useRecoilCallback(
    ({ snapshot, set }) =>
      () => {
        set(designLocalState, getFrom(snapshot)(designRemoteState))
      },
    [],
  )

  const resetLocal = useRecoilCallback(
    ({ snapshot, set }) =>
      () => {
        loadDesignIntoElements(set, getFrom(snapshot)(designRemoteState))
      },
    [],
  )

  const actions = useMemo(
    () => ({ setLocal, changeLocal, save, reset, resetLocal, resetRemote }),
    [setLocal, changeLocal, save, reset, resetLocal, resetRemote],
  )
  const status = useMemo(
    () => ({ isSaving, isResetting, isSuccess }),
    [isSaving, isResetting, isSuccess],
  )

  const whenIdle = useCallback(() => inFlightRef.current, [])

  return { actions, status, whenIdle }
}
